/**
 * useCms - hook to read editorial content from /api/cms.
 *
 * Strategy:
 * - Fetches all CMS content once, caches in module-level state
 * - Components call useCms("home.hero.title", "Default Title") and get the live value
 * - If API fails, fallback string is returned (zero-blast-radius)
 * - Auto-refresh on 'realty237:cms-changed' window event (admin saves trigger this)
 */
import { useEffect, useState } from 'react';
import { cmsAPI } from '../utils/api';

let _cache = null;
let _loading = null;
const _subscribers = new Set();

const _notifySubscribers = () => {
  _subscribers.forEach((fn) => {
    try { fn(); } catch (_e) { /* ignore */ }
  });
};

export const refreshCmsCache = async () => {
  try {
    const res = await cmsAPI.getPublic();
    _cache = res.data || {};
    _notifySubscribers();
  } catch (_e) {
    // keep prior cache or empty
  }
};

const _ensureLoaded = () => {
  if (_cache !== null) return Promise.resolve();
  if (_loading) return _loading;
  _loading = refreshCmsCache().finally(() => { _loading = null; });
  return _loading;
};

export const useCms = (key, fallback = '') => {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    let mounted = true;
    const update = () => { if (mounted) forceUpdate((n) => n + 1); };
    _subscribers.add(update);
    _ensureLoaded();
    return () => {
      mounted = false;
      _subscribers.delete(update);
    };
  }, []);

  if (_cache === null) return fallback;
  const v = _cache[key];
  return (v === undefined || v === null || v === '') ? fallback : v;
};

// Listen for global change events (e.g., from admin save)
if (typeof window !== 'undefined') {
  window.addEventListener('realty237:cms-changed', () => { refreshCmsCache(); });
}
